import { FinanceType } from '@upstart/harmony/lib/legos/constants';
import { isMarketScanPaymentsEnabled } from '@upstart/harmony/lib/dealer-utils';
import { TradeInTaxCreditArg } from './types';

/**
 * Use this function to determine if trade-in tax credit is available for the given state and finance type.
 *
 * Example usage scenarios:
 * Scenario 1: Display Check box
 * ```js
 * const shouldDisplayTradeInCheckBox = isTradeInTaxCreditAvailable()
 * if(shouldDisplayTradeInCheckBox) { ... }
 * ...
 *
 * Scenario 2: Display legal notice in the footer or sidebar
 * ```js
 * const shouldDisplayTradeInTaxCreditLegalNotice = isTradeInTaxCreditAvailable()
 * if(shouldDisplayTradeInTaxCreditLegalNotice) { // display legal notice }
 * ```
 *
 * Scenario 3: Display a Message let's say during the trade in process
 * ```js
 * const shouldDisplayTradeInTaxCreditToastMessage = isTradeInTaxCreditAvailable()
 * if (shouldDisplayTradeInTaxCreditToastMessage) {
 *     displayToast('Hey! You are eligible for tradein tax credit in your state!')
 * }
 * ```
 *
 * @param dealer
 * @param deal
 * @param offer
 * @param financeType
 */
export const isTradeInTaxCreditAvailable = ({
  dealer,
  deal,
  offer,
  financeType,
}: TradeInTaxCreditArg): {
  isTradeInTaxCreditAvailable: boolean;
  state?: string;
} => {
  if (
    financeType === FinanceType.LEASE &&
    !isMarketScanPaymentsEnabled(dealer)
  ) {
    return { isTradeInTaxCreditAvailable: false };
  }

  const taxByDealerAddress = offer?.data?.taxBreakdown?.taxByDealerAddress;
  const applicantState = deal?.applicant?.state?.trim().toUpperCase();
  const dealerState = dealer?.state?.trim().toUpperCase();

  const stateForTaxCredit = taxByDealerAddress
    ? dealerState
    : applicantState || dealerState;

  if (!stateForTaxCredit) {
    // state is not available, so we can't determine if tax credit is applicable
    return { isTradeInTaxCreditAvailable: true };
  }

  if (
    !isTradeInTaxCreditAvailableForStateAndFinanceType(
      stateForTaxCredit,
      financeType,
    )
  ) {
    return { isTradeInTaxCreditAvailable: false, state: stateForTaxCredit };
  }
  return { isTradeInTaxCreditAvailable: true, state: stateForTaxCredit };
};

/**
 *
 * @param state
 * @param financeType
 */
function isTradeInTaxCreditAvailableForStateAndFinanceType(
  state: string,
  financeType: FinanceType,
) {
  // these states do not provide tax credit for retail and lease
  const statesWithNoTradeInTaxCredit = [
    'AK',
    'ALASKA',
    'CA',
    'CALIFORNIA',
    'DC',
    'DISTRICT OF COLUMBIA',
    'MT',
    'MONTANA',
    'NH',
    'NEW HAMPSHIRE',
    'OR',
    'OREGON',
  ];

  // these states do not provide tax credit only for lease
  const statesWithNoTradeInTaxCreditForLease = [
    'HI',
    'HAWAII',
    'LA',
    'LOUISIANA',
    'MI',
    'MICHIGAN',
    'NV',
    'NEVADA',
  ];

  const stateInUpperCase = state.trim().toUpperCase();
  if (statesWithNoTradeInTaxCredit.includes(stateInUpperCase)) {
    return false;
  }

  if (
    financeType === FinanceType.LEASE &&
    statesWithNoTradeInTaxCreditForLease.includes(stateInUpperCase)
  ) {
    return false;
  }

  // for rest return true
  return true;
}
